import { spacing } from '../design/tokens.ts'
import type { TokenizedCard } from './types.ts'
import { isRecord } from './guards.ts'

export const VGS_COLLECT = {
  version: '3.2.1',
  // Matches js.verygoodvault.com only; the js3 fallback serves different bytes, so SRI fails closed there.
  integrity:
    'sha384-LwaMuUQqEXi3Pu8zWZGhPKirAnX1OO8Q1KPozJQgIcai6c01CFT/oAtGPoO+0PpW',
  submitPath: '/post',
  fields: {
    number: 'card-number',
    cvc: 'card-security-code',
    // The /post route aliases card-expiration-date, so this name is card-expiry.
    expiry: 'card-expiry'
  }
} as const

export interface CardFieldTargets {
  number: string
  cvc: string
  expiry: string
}

export type CardFieldKey = keyof CardFieldTargets

export interface InvalidCardField {
  field: CardFieldKey
  empty: boolean
}

/** Collect refused the form; `fields` names the refused fields in form order and holds no card data. */
export class CardFieldsInvalidError extends Error {
  readonly fields: readonly InvalidCardField[]

  constructor(fields: readonly InvalidCardField[]) {
    super('card details are not valid')
    this.name = 'CardFieldsInvalidError'
    this.fields = fields
  }
}

/** Host classes that VGS and the mock set. Focus in a cross-origin frame does not match :focus-within. */
export const CARD_FIELD_CLASSES = {
  focused: 'terminal-card-field--focused',
  invalid: 'terminal-card-field--invalid',
  touched: 'terminal-card-field--touched'
} as const

/** Host class for a card field while the form is saving. */
export const CARD_FIELD_DISABLED_CLASS = 'terminal-card-field--disabled'

export interface CardFieldHostState {
  isFocused: boolean
  isValid: boolean
  isTouched: boolean
}

export const FIELD_READY_TIMEOUT_MS = 10_000

/** A secure field did not become ready in time. */
export class CardFieldsNotReadyError extends Error {
  constructor() {
    super('card fields did not become ready')
    this.name = 'CardFieldsNotReadyError'
  }
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return isRecord(value) && typeof value.then === 'function'
}

function fieldReady(field: unknown): PromiseLike<unknown> {
  const promise = isRecord(field) ? field.promise : undefined
  return isPromiseLike(promise)
    ? promise
    : Promise.reject(new CardFieldsNotReadyError())
}

/**
 * Waits until every secure field has loaded. A Collect field's `promise` resolves when its iframe posts `loaded`.
 * @param fields - Collect fields, or the mock's stand-ins.
 * @param timeoutMs - How long to wait before failing.
 * @returns The ready promise, and a cancel function that resolves it at once.
 * @throws {CardFieldsNotReadyError} When a field is missing, fails or is too slow.
 */
export function whenFieldsReady(
  fields: readonly unknown[],
  timeoutMs = FIELD_READY_TIMEOUT_MS
): { done: Promise<void>; cancel: () => void } {
  let finish: (ready: boolean) => void
  const done = new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => finish(false), timeoutMs)
    finish = ready => {
      clearTimeout(timer)
      if (ready) resolve()
      else reject(new CardFieldsNotReadyError())
    }
  })
  Promise.all(fields.map(fieldReady)).then(
    () => finish(true),
    () => finish(false)
  )
  return { done, cancel: () => finish(true) }
}

export const MAX_NAME_ON_CARD = 100

export const NAME_ON_CARD_PROBLEM = 'Enter the name on the card.'

const FORM_ORDER: readonly CardFieldKey[] = ['number', 'expiry', 'cvc']

const FIELD_PROBLEMS: Record<CardFieldKey, { empty: string; invalid: string }> =
  {
    number: {
      empty: 'Enter the card number.',
      invalid: 'Check the card number.'
    },
    expiry: {
      empty: 'Enter the expiry as MM / YY or MM / YYYY.',
      invalid: 'Check the expiry. Enter it as MM / YY or MM / YYYY.'
    },
    cvc: { empty: 'Enter the CVV.', invalid: 'Check the CVV.' }
  }

/**
 * Returns the copy for a failed tokenize: the refused fields, or the general message.
 * @param error - The tokenize rejection.
 * @returns One user-facing message.
 */
export function cardProblem(error: unknown): string {
  if (!(error instanceof CardFieldsInvalidError) || error.fields.length === 0) {
    return 'Check the card details and try again.'
  }
  return error.fields
    .map(f => FIELD_PROBLEMS[f.field][f.empty ? 'empty' : 'invalid'])
    .join(' ')
}

export interface CardFieldsAdapter {
  kind: 'vgs' | 'mock'
  mount: (
    targets: CardFieldTargets,
    css: Record<string, unknown>
  ) => Promise<void>
  tokenize: () => Promise<TokenizedCard>
  destroy: () => void
}

export interface VaultConfig {
  vaultId: string
  environment: string
  version: string
  integrity: string
  crossorigin: 'anonymous'
}

export type LoadCollect = (config: VaultConfig) => Promise<unknown>

interface FieldState {
  isValid?: boolean
  last4?: string
  cardType?: string
}

interface CollectForm {
  field(selector: string, options: Record<string, unknown>): unknown
  submit(
    path: string,
    options: Record<string, unknown>,
    onSuccess: (status: number, data: unknown) => void,
    onError: (errors: unknown) => void
  ): void
  state?: Record<string, FieldState | undefined>
  unmount?(): void
}

interface CollectGlobal {
  init(onState?: (state: unknown) => void): CollectForm
}

/** The loader the terminal ships with; the dynamic import keeps VGS out of the entry chunk. */
export const loadVaultCollect: LoadCollect = async config => {
  const { loadVGSCollect } = await import('@vgs/collect-js')
  return loadVGSCollect(config)
}

function isCollect(value: unknown): value is CollectGlobal {
  return isRecord(value) && typeof value.init === 'function'
}

function pick(data: unknown, key: string): unknown {
  if (!isRecord(data)) return undefined
  const json = data.json
  if (isRecord(json) && key in json) return json[key]
  return data[key]
}

function refusal(fieldState: unknown): { empty: boolean } | null {
  if (!isRecord(fieldState) || fieldState.isValid !== false) return null
  return { empty: fieldState.isEmpty === true }
}

function toInt(value: unknown): number | null {
  const n = typeof value === 'string' ? Number.parseInt(value, 10) : value
  return typeof n === 'number' && Number.isInteger(n) ? n : null
}

function toYear(value: unknown): number | null {
  const year = toInt(value)
  return year !== null && /^\d{2}$/.test(String(value)) ? 2000 + year : year
}

/**
 * Creates card fields backed by VGS Collect, so card data stays in VGS iframes.
 * @param vault - The vault id and environment.
 * @param load - The Collect loader; tests pass a fake.
 * @param readyTimeoutMs - How long a field may take to become ready.
 * @returns The adapter.
 */
export function createVgsCardFields(
  vault: { vaultId: string; environment: string },
  load: LoadCollect = loadVaultCollect,
  readyTimeoutMs = FIELD_READY_TIMEOUT_MS
): CardFieldsAdapter {
  let form: CollectForm | null = null
  let epoch = 0
  let cancelReady: (() => void) | undefined
  const { fields } = VGS_COLLECT

  return {
    kind: 'vgs',
    async mount(targets, css) {
      const mine = epoch
      const collect = await load({
        vaultId: vault.vaultId,
        environment: vault.environment,
        version: VGS_COLLECT.version,
        integrity: VGS_COLLECT.integrity,
        crossorigin: 'anonymous'
      })
      if (!isCollect(collect)) throw new Error('VGS Collect did not load')
      if (mine !== epoch) return
      form = collect.init()
      const numberField = form.field(`#${targets.number}`, {
        name: fields.number,
        type: 'card-number',
        validations: ['required', 'validCardNumber'],
        placeholder: '1234 5678 9012 3456',
        autoComplete: 'cc-number',
        showCardIcon: {
          width: spacing.space6,
          height: 'auto',
          right: spacing.space4
        },
        classes: CARD_FIELD_CLASSES,
        css: {
          ...css,
          'padding-right': `calc(${spacing.space8} + ${spacing.space2})`
        }
      })
      const cvcField = form.field(`#${targets.cvc}`, {
        name: fields.cvc,
        type: 'card-security-code',
        validations: ['required', 'validCardSecurityCode'],
        placeholder: 'CVV',
        autoComplete: 'cc-csc',
        classes: CARD_FIELD_CLASSES,
        css
      })
      const expiryField = form.field(`#${targets.expiry}`, {
        name: fields.expiry,
        type: 'card-expiration-date',
        validations: ['required', 'validCardExpirationDate'],
        // No yearLength: Collect then takes and validates a 2-digit or a 4-digit year.
        placeholder: 'MM / YY',
        autoComplete: 'cc-exp',
        serializers: [
          {
            name: 'separate',
            options: { monthName: 'expMonth', yearName: 'expYear' }
          }
        ],
        classes: CARD_FIELD_CLASSES,
        css
      })
      const ready = whenFieldsReady(
        [numberField, cvcField, expiryField],
        readyTimeoutMs
      )
      cancelReady = ready.cancel
      try {
        await ready.done
      } catch (error) {
        form.unmount?.()
        form = null
        throw error
      }
    },
    tokenize() {
      const active = form
      if (!active)
        return Promise.reject(new Error('card fields are not mounted'))
      return new Promise<TokenizedCard>((resolve, reject) => {
        active.submit(
          VGS_COLLECT.submitPath,
          { method: 'POST' },
          (status, data) => {
            const card = active.state?.[fields.number]
            const expiry = pick(data, fields.expiry)
            const cardAlias = pick(data, fields.number)
            const cvvAlias = pick(data, fields.cvc)
            const expMonth = toInt(pick(expiry, 'expMonth'))
            const expYear = toYear(pick(expiry, 'expYear'))
            if (
              status < 200 ||
              status >= 300 ||
              typeof cardAlias !== 'string' ||
              typeof cvvAlias !== 'string' ||
              typeof card?.last4 !== 'string' ||
              expMonth === null ||
              expYear === null
            ) {
              reject(new Error('card fields returned no usable aliases'))
              return
            }
            resolve({
              cardAlias,
              cvvAlias,
              brand: card.cardType ?? null,
              last4: card.last4,
              expMonth,
              expYear
            })
          },
          errors => {
            const refused = FORM_ORDER.flatMap(field => {
              const name = fields[field]
              const hit =
                refusal(isRecord(errors) ? errors[name] : undefined) ??
                refusal(active.state?.[name])
              return hit ? [{ field, ...hit }] : []
            })
            reject(
              refused.length > 0
                ? new CardFieldsInvalidError(refused)
                : new Error('card details are not valid')
            )
          }
        )
      })
    },
    destroy() {
      epoch += 1
      cancelReady?.()
      cancelReady = undefined
      form?.unmount?.()
      form = null
    }
  }
}
